import React, { useState, useEffect } from 'react';
import styles from './TopologyToolbar.module.css';
import historyManager from '../history/historyManager';

const ScissorsIcon = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="6" cy="6" r="3" />
        <circle cx="6" cy="18" r="3" />
        <line x1="20" y1="4" x2="8.12" y2="15.88" />
        <line x1="14.47" y1="14.48" x2="20" y2="20" />
        <line x1="8.12" y1="8.12" x2="12" y2="12" />
    </svg>
);

const MergeIcon = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="12" height="12" rx="2" />
        <rect x="10" y="10" width="12" height="12" rx="2" />
    </svg>
);

const CopyIcon = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
);

const UndoIcon = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 7v6h6" />
        <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
    </svg>
);

const RedoIcon = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 7v6h-6" />
        <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3l3 2.7" />
    </svg>
);

export const TopologyToolbar = ({
    selectedFeatures = [],
    hasActiveFeature = false,
    activeAction = null,
    onDifference,
    onUnion,
    onDuplicate,
    onUndo,
    onRedo
}) => {
    const [historyState, setHistoryState] = useState(historyManager.getState());

    useEffect(() => {
        const unsubscribe = historyManager.subscribe(setHistoryState);
        return () => unsubscribe();
    }, []);

    const count = selectedFeatures.length;
    const canDifference = hasActiveFeature || count === 2;
    const canUnion = hasActiveFeature || count >= 2;

    const isVisible = hasActiveFeature || count > 0;

    if (!isVisible) return null;

    return (
        <div className={styles.topologyToolbar}>
            {onDuplicate && (
                <button
                    className={styles.toolbarButton}
                    onClick={onDuplicate}
                    disabled={!hasActiveFeature}
                    title={hasActiveFeature ? "Дублировать объект (Shift для стыковки по 4 сторонам)" : "Выберите объект для дублирования"}
                >
                    <CopyIcon size={20} />
                </button>
            )}

            <button
                className={`${styles.toolbarButton} ${activeAction === 'difference' ? styles.active : ''}`}
                onClick={onDifference}
                disabled={!canDifference}
                title={
                    activeAction === 'difference'
                        ? "Режим вычитания активен: кликните на 2-й объект на карте (Esc для отмены)"
                        : hasActiveFeature
                        ? "Вычесть: нажмите и кликните на второй участок на карте"
                        : "Вычесть контур (выберите 2 объекта)"
                }
            >
                <ScissorsIcon size={20} />
            </button>

            <button
                className={`${styles.toolbarButton} ${activeAction === 'union' ? styles.active : ''}`}
                onClick={onUnion}
                disabled={!canUnion}
                title={
                    activeAction === 'union'
                        ? "Режим объединения активен: кликните на объект на карте (Esc для отмены)"
                        : hasActiveFeature
                        ? "Объединить: нажмите и кликните на смежный участок на карте"
                        : "Объединить участки"
                }
            >
                <MergeIcon size={20} />
                {count > 1 && <span className={styles.badge}>{count}</span>}
            </button>

            {(historyState.canUndo || historyState.canRedo) && (
                <>
                    <div className={styles.divider} />

                    <button
                        className={styles.toolbarButton}
                        onClick={onUndo}
                        disabled={!historyState.canUndo}
                        title="Отменить действие (Ctrl+Z)"
                    >
                        <UndoIcon size={20} />
                        {historyState.undoCount > 0 && (
                            <span className={styles.badge}>{historyState.undoCount}</span>
                        )}
                    </button>

                    <button
                        className={styles.toolbarButton}
                        onClick={onRedo}
                        disabled={!historyState.canRedo}
                        title="Повторить действие (Ctrl+Y)"
                    >
                        <RedoIcon size={20} />
                        {historyState.redoCount > 0 && (
                            <span className={styles.badge}>{historyState.redoCount}</span>
                        )}
                    </button>
                </>
            )}
        </div>
    );
};

export default TopologyToolbar;
